import { useQuery } from '@apollo/client';
import {
  MISSION_HEADER,
  MISSION_PRICING,
  MISSION_CONTACTS,
  MISSION_SOCIETIES,
  MISSION_PURCHASE_ORDERS,
  GET_CRA_BY_MISSION,
  GET_LEGAL_CONTRACTS_BY_MISSION,
} from '@bloomays-lib/adapter.api-bloomer';
import type { IContact, IPricing, ISociety, ResolvedContact, InvoiceStatus } from '@bloomays-lib/types.shared';
import { MAIN_CONTRACT_STEPS } from '@bloomays-lib/utils.shared';
import type { DashboardPurchaseOrder } from '../helpers/purchaseOrderDashboard';

// Le tableau de bord ne s'intéresse qu'au contrat PRINCIPAL (pas aux avenants « Avenant N ») : on filtre
// côté serveur (steps + types) pour ne rapatrier que lui — évite de cibler un avenant lors de l'upload signé.
const PRINCIPAL_CONTRACT_TYPES = ['PDS', 'SST'];

/**
 * Chargement asynchrone par section du Tableau de bord de mission V2 (TEC-3051).
 *
 * Tire en parallèle les requêtes consolidées exposées par TEC-3052 (même root
 * `missions(recordId)`) + le CRA, et expose des données typées + l'état (loading/error)
 * par section pour un rendu progressif. `refetchAll` réinvalide tout (bouton « Rafraîchir »).
 */

type AccountManagerLite = { recordId?: string; fullname?: string } | null;

export type DashboardBloomer = {
  recordId?: string;
  fullname?: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  secondaryEmail?: string;
  phoneNumber?: string;
  slack?: string;
  credentialID?: string;
  accountManager?: AccountManagerLite;
} | null;

export type DashboardHeaderMission = {
  recordId: string;
  externalId?: string;
  jobId?: string;
  origin?: string;
  label?: string;
  /** Code mission repris dans les BDC client (trigramme) — éditable librement. */
  missionCode?: string;
  type?: string;
  isActive: boolean;
  beginMission: string;
  endMission: string;
  contractType?: string;
  hoursPerDays: number;
  clientPaydateDays?: number;
  bloomerPaydateDays?: number;
  replacement?: boolean | null;
  shouldBeCountAsDeal?: boolean;
  enableCRAUpload?: boolean;
  enableBusinessReferalNpsClient?: boolean;
  enableBusinessReferalNpsBloomer?: boolean;
  CSM?: { fullname?: string } | null;
  jobOwner?: { firstname?: string; lastname?: string; email?: string; role?: string } | null;
  bloomer?: DashboardBloomer;
  extension?: { assessment?: number; newEndDate?: string } | null;
};

export type DashboardPricingMission = {
  recordId: string;
  pricing?: IPricing[];
};

export type DashboardContactsMission = {
  recordId: string;
  // SDL : `contactOperations: Contact` (objet unique, client mission-only).
  contactOperations?: IContact;
  clientLegalContacts?: IContact[];
  bloomerLegalContacts?: IContact[];
  billingContacts?: IContact[];
  resolvedClientPdsContact?: ResolvedContact;
  resolvedClientBillingContacts?: ResolvedContact[];
  resolvedSupplierSstContact?: ResolvedContact;
};

export type DashboardSocietiesMission = {
  recordId: string;
  clientSociety?: ISociety;
  clientBillingSociety?: ISociety;
  // SDL : `bloomerBillingSociety: [Society]` (liste résolue depuis les recordIds).
  bloomerBillingSociety?: ISociety[];
};

export type DashboardPurchaseOrdersMission = {
  recordId: string;
  purchaseOrderRequired?: boolean;
  effectivePurchaseOrderRequired?: boolean;
  purchaseOrders?: DashboardPurchaseOrder[];
};

export type DashboardCraActivity = {
  missionId?: string;
  activityId?: string;
  activityMonth?: string;
  status?: string;
  nbDaysWorked?: number;
  nbDaysMarkupWorked?: number;
  nbDaysMarkup2Worked?: number;
  /** Détail jour-par-jour (JSON `ActivityNbDayDetails`) pour la grille calendaire. */
  nbDaysDetails?: string;
  hoursPerDays?: number;
  clientInvoiceBilled?: boolean;
  clientInvoiceStatus?: InvoiceStatus | null;
  bloomerInvoiceStatus?: InvoiceStatus | null;
  bloomerScore?: number;
  bloomerComment?: string;
  bloomerScoreDate?: string;
  clientScore?: number;
  clientComment?: string;
  clientScoreDate?: string;
};

/** Contrat légal (PDS / SST) renvoyé par `getLegalContractsByMission`. */
export type DashboardLegalContract = {
  id?: string;
  recordId?: string;
  type?: string;
  status?: string;
  step?: string;
  signedDate?: string;
  nbSigner?: number;
  signedCount?: number;
  signedFileUrl?: string;
  emptyFileUrl?: string;
  signLink?: string;
};

type SectionState<T> = {
  data?: T;
  loading: boolean;
  error?: Error;
};

export type UseMissionDashboardResult = {
  missionId: string;
  loading: boolean;
  header: SectionState<DashboardHeaderMission>;
  pricing: SectionState<DashboardPricingMission>;
  contacts: SectionState<DashboardContactsMission>;
  societies: SectionState<DashboardSocietiesMission>;
  purchaseOrders: SectionState<DashboardPurchaseOrdersMission>;
  cra: SectionState<DashboardCraActivity[]>;
  contracts: SectionState<DashboardLegalContract[]>;
  refetchAll: () => Promise<unknown>;
};

const first = <T>(rows?: { missions?: T[] } | null): T | undefined => rows?.missions?.[0];

export const useMissionDashboard = (missionId: string): UseMissionDashboardResult => {
  const sharedVars = { variables: { recordId: missionId }, notifyOnNetworkStatusChange: true };

  const header = useQuery<{ missions: DashboardHeaderMission[] }>(MISSION_HEADER, sharedVars);
  const pricing = useQuery<{ missions: DashboardPricingMission[] }>(MISSION_PRICING, sharedVars);
  const contacts = useQuery<{ missions: DashboardContactsMission[] }>(MISSION_CONTACTS, sharedVars);
  const societies = useQuery<{ missions: DashboardSocietiesMission[] }>(MISSION_SOCIETIES, sharedVars);
  const purchaseOrders = useQuery<{ missions: DashboardPurchaseOrdersMission[] }>(MISSION_PURCHASE_ORDERS, sharedVars);
  const cra = useQuery<{ getCraByMission: DashboardCraActivity[] }>(GET_CRA_BY_MISSION, {
    variables: { missionId },
    notifyOnNetworkStatusChange: true,
  });
  const contractsQuery = useQuery<{ getLegalContractsByMission?: { contracts?: DashboardLegalContract[] } }>(
    GET_LEGAL_CONTRACTS_BY_MISSION,
    {
      variables: { missionId, filter: { steps: [...MAIN_CONTRACT_STEPS], types: PRINCIPAL_CONTRACT_TYPES } },
      notifyOnNetworkStatusChange: true,
    },
  );

  const refetchAll = () =>
    Promise.all([
      header.refetch(),
      pricing.refetch(),
      contacts.refetch(),
      societies.refetch(),
      purchaseOrders.refetch(),
      cra.refetch(),
      contractsQuery.refetch(),
    ]);

  return {
    missionId,
    loading: header.loading && !header.data,
    header: { data: first(header.data), loading: header.loading, error: header.error },
    pricing: { data: first(pricing.data), loading: pricing.loading, error: pricing.error },
    contacts: { data: first(contacts.data), loading: contacts.loading, error: contacts.error },
    societies: { data: first(societies.data), loading: societies.loading, error: societies.error },
    purchaseOrders: { data: first(purchaseOrders.data), loading: purchaseOrders.loading, error: purchaseOrders.error },
    cra: { data: cra.data?.getCraByMission, loading: cra.loading, error: cra.error },
    contracts: {
      data: contractsQuery.data?.getLegalContractsByMission?.contracts,
      loading: contractsQuery.loading,
      error: contractsQuery.error,
    },
    refetchAll,
  };
};
